// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Ship Modules Configuration
 * 
 * Defines installable ship modules that provide passive bonuses.
 * Modules are purchased at stations and provide permanent benefits.
 */

export const shipModules = {
  // Required for trading
  cargoBay: {
    id: 'cargoBay',
    name: 'Cargo Bay',
    icon: '📦',
    description: 'Adds cargo storage capacity for trading goods between systems',
    cost: 5000,
    tier: 1,
    category: 'utility',
    
    effects: {
      cargoCapacity: 50 // Can carry 50 units of goods
    },
    
    availableAt: ['station'],
    requires: null
  },

  // Cargo expansion
  expandedCargoBay: {
    id: 'expandedCargoBay',
    name: 'Expanded Cargo Bay',
    icon: '📦📦',
    description: 'Doubles cargo storage capacity for larger trading operations',
    cost: 8000,
    tier: 2,
    category: 'utility',
    
    effects: {
      cargoCapacityMultiplier: 3 // Triples cargo capacity
    },
    
    availableAt: ['station'],
    requires: 'cargoBay'
  },

  // Auto-repair system
  autoRepair: {
    id: 'autoRepair',
    name: 'Auto-Repair System',
    icon: '🔧',
    description: 'Automatically repairs hull damage over time when not in combat',
    cost: 6000,
    tier: 1,
    category: 'survival',
    
    effects: {
      repairRate: 2 // Repairs 2 HP every 10 seconds
    },
    
    availableAt: ['station'],
    requires: null
  },

  // Advanced scanner
  advancedScanner: {
    id: 'advancedScanner',
    name: 'Advanced Scanner Array',
    icon: '📡',
    description: 'Reveals hidden opportunities and threats during warp travel',
    cost: 4500,
    tier: 1,
    category: 'exploration',
    
    effects: {
      detectHiddenEvents: true,
      scanBonus: 0.25 // +25% scan effectiveness
    },
    
    availableAt: ['station'],
    requires: null
  },

  // Mining laser
  miningLaser: {
    id: 'miningLaser',
    name: 'Mining Laser',
    icon: '⛏️',
    description: 'Extract rare minerals from asteroid fields during exploration',
    cost: 7000,
    tier: 2,
    category: 'utility',
    
    effects: {
      enableMining: true,
      miningBonus: 0.5 // 50% more minerals from mining events
    },
    
    availableAt: ['station'],
    requires: null
  },

  // Stealth generator
  stealthGenerator: {
    id: 'stealthGenerator',
    name: 'Stealth Generator',
    icon: '👻',
    description: 'Reduces chance of hostile encounters during warp travel',
    cost: 9000,
    tier: 2,
    category: 'survival',
    
    effects: {
      combatAvoidance: 0.3 // 30% chance to avoid combat encounters
    },
    
    availableAt: ['station'],
    requires: null
  }
};

// Helper to get all modules as array
export const getAllModules = () => {
  return Object.values(shipModules);
};

// Helper to get module by id
export const getModule = (id) => {
  return shipModules[id];
};

// Helper to get modules available at location
export const getModulesForLocation = (locationType) => {
  return getAllModules().filter(module => 
    module.availableAt.includes(locationType)
  );
};

export default shipModules;
